import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import {
  tenantAPI,
  type Zone,
  type CreateZoneData,
  type UpdateZoneData
} from '@vehix/portal/services/api/tenants';
import { useAuth } from '@vehix/shared/admin/portal-bridge';

// Query keys for consistent caching
export const zoneKeys = {
  all: ['zones'] as const,
  lists: () => [...zoneKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...zoneKeys.lists(), { filters }] as const,
  details: () => [...zoneKeys.all, 'detail'] as const,
  detail: (id: string) => [...zoneKeys.details(), id] as const,
  zipCodes: (id: string) => [...zoneKeys.detail(id), 'zipCodes'] as const,
  // Include tenantId in cache key so different tenants have separate cached counts
  currentRecalls: (ids: string[], tenantId?: string) =>
    [...zoneKeys.all, 'currentRecalls', { ids, tenantId }] as const
};

/**
 * Hook to fetch all zones with caching
 * Cache duration: 10 minutes for zone list
 * Only runs when session is established to ensure JWT is available for RPC calls
 */
export function useZones() {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: zoneKeys.lists(),
    queryFn: () => tenantAPI.getAllZonesWithZipCounts(),
    // Stale-while-revalidate posture (gold standard for rarely-changing
    // reference data): show cached rows instantly on every navigation, only
    // refetch in the background when the cache is genuinely stale, never
    // block UI on revalidation.
    staleTime: 10 * 60 * 1000, // 10 minutes - zones don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnMount: true, // Refetch in background when stale; staleTime guards against over-fetching
    refetchOnReconnect: true, // Do refetch on network reconnect
    retry: 2,
    // keepPreviousData prevents the table flashing to skeleton on background
    // refetches (e.g. after a zone create/update/delete invalidates the cache).
    placeholderData: keepPreviousData,
    // Only fetch when session is established (ensures JWT is available for RPC call)
    enabled: !!session && !authLoading
  });
}

/**
 * Hook to fetch paginated zones with caching
 * Follows same pattern as useCampaigns and useDealers for consistency
 * Only runs when session is established to ensure JWT is available for RPC calls
 *
 * @param oemFilter - Optional OEM filter: single string or array of strings (undefined = all OEMs)
 */
export function useZonesPaginated(
  page: number = 1,
  limit: number = 50,
  searchTerm?: string,
  statusFilter?: 'all' | 'active' | 'inactive',
  regionFilter?: string,
  oemFilter?: string | string[], // Filter by OEM(s) - single string or array (undefined = all OEMs)
  includeUserNames: boolean = false // Skip expensive user name lookup for faster initial load
) {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: zoneKeys.list({
      page,
      limit,
      searchTerm,
      statusFilter,
      regionFilter,
      oemFilter, // Include in cache key (works with both string and array)
      includeUserNames
    }),
    queryFn: () =>
      tenantAPI.getZonesPaginated(
        page,
        limit,
        searchTerm,
        statusFilter,
        regionFilter,
        oemFilter, // Pass OEM filter (API now handles both string and array)
        includeUserNames
      ),
    staleTime: 10 * 60 * 1000, // 10 minutes - zones change infrequently (same as campaigns)
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnMount: false, // Use cached data if available
    retry: 2,
    // Only fetch when session is established (ensures JWT is available for RPC call)
    enabled: !!session && !authLoading
  });
}

/**
 * Hook to fetch zone ZIP codes with caching
 */
export function useZoneZipCodes(zoneId: string) {
  return useQuery({
    queryKey: zoneKeys.zipCodes(zoneId),
    queryFn: () => tenantAPI.getZoneZipCodes(zoneId),
    staleTime: 15 * 60 * 1000, // 15 minutes - ZIP codes change less frequently
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!zoneId
  });
}

/**
 * Hook to fetch current recall counts and data collection zones for multiple zones.
 * Returns { counts, dataCollectionZones, snapshotDates, oldestSnapshotDates },
 * all keyed by zone id, from a single RPC call for consistency.
 *
 * Counts are tenant-scoped by the server, which resolves each zone's assigned tenant
 * itself — callers do not pass one. `tenantId` is an override for the rare case of
 * previewing a zone against a tenant it is not assigned to.
 *
 * The RPC is set-shaped on the server (`WHERE zone_id = ANY($1)`), so the response
 * is identical for any permutation of the same zone-id set. We canonicalize the
 * input (sorted + de-duped) so the React Query cache key collapses N permutations
 * into one entry — same set of IDs in any order shares one cache slot.
 */
export function useCurrentRecallCounts(zoneIds: string[], tenantId?: string) {
  const { session, loading: authLoading } = useAuth();
  const canonicalIds = useMemo(() => [...new Set(zoneIds)].sort(), [zoneIds]);

  return useQuery({
    queryKey: zoneKeys.currentRecalls(canonicalIds, tenantId),
    queryFn: () => tenantAPI.getCurrentRecallCountsForZones(canonicalIds, tenantId),
    enabled: canonicalIds.length > 0 && !!session && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes - recalls change with syncs
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });
}

/**
 * Hook to fetch tenant mappings for zones (zone_id → tenant_name).
 * Used by the zone list to show which tenant each zone is mapped to.
 */
export function useZoneTenantMappings(zoneIds: string[]) {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: [...zoneKeys.all, 'tenantMappings', { ids: zoneIds }],
    queryFn: () => tenantAPI.getZoneTenantMappings(zoneIds),
    enabled: zoneIds.length > 0 && !!session && !authLoading,
    staleTime: 10 * 60 * 1000, // 10 minutes - mappings change infrequently
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

/**
 * When each zone was assigned to its current tenant (zone_id -> ISO timestamp).
 *
 * Deliberately unfiltered and stable-keyed. prod.tenant_zones is ~120 rows, so
 * one cached fetch serves every tenant dialog; keying on a zone-id set instead
 * would miss the cache every time the selection changed. The key sits under
 * zoneKeys.all, so the tenant-save invalidation clears it along with the rest.
 */
export function useZoneAssignmentDates() {
  const { session, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: [...zoneKeys.all, 'assignmentDates'],
    queryFn: () => tenantAPI.getZoneAssignmentDates(),
    enabled: !!session && !authLoading,
    staleTime: 10 * 60 * 1000, // 10 minutes - assignments change infrequently
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to refresh denormalized current recall counts for all active zones.
 */
export function useRefreshZoneRecallCounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => tenantAPI.refreshZoneRecallCounts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: zoneKeys.lists() });
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });
}

/**
 * Hook for zone mutations (create, update, delete)
 */
export function useZoneMutations() {
  const queryClient = useQueryClient();

  const createZone = useMutation({
    mutationFn: ({ data, userId }: { data: CreateZoneData; userId?: string }) =>
      tenantAPI.createZone(data, userId),
    onSuccess: newZone => {
      // Update the zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) => [...old, newZone]);
      // Invalidate paginated queries to refresh counts
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
      // Invalidate all lists to be safe
      queryClient.invalidateQueries({ queryKey: zoneKeys.lists() });
    }
  });

  const updateZone = useMutation({
    mutationFn: ({ id, data, userId }: { id: string; data: UpdateZoneData; userId?: string }) =>
      tenantAPI.updateZone(id, data, userId),
    onSuccess: (updatedZone, { id }) => {
      // Update the zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) =>
        old.map(zone => (zone.id === id ? { ...zone, ...updatedZone } : zone))
      );
      // Update specific zone cache if it exists
      queryClient.setQueryData(zoneKeys.detail(id), updatedZone);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });

  const deleteZone = useMutation({
    mutationFn: ({ id, hard, userId }: { id: string; hard?: boolean; userId?: string }) =>
      tenantAPI.deleteZone(id, hard, userId),
    onSuccess: (_, { id }) => {
      // Remove from zones list cache
      queryClient.setQueryData(zoneKeys.lists(), (old: Zone[] = []) =>
        old.filter(zone => zone.id !== id)
      );
      // Remove specific zone cache
      queryClient.removeQueries({ queryKey: zoneKeys.detail(id) });
      // Invalidate paginated queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.list({}) });
    }
  });

  return {
    createZone,
    updateZone,
    deleteZone
  };
}
